import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import Question from '../../../../components/CASAForm/Question';
import CheckboxOption from "../../../../components/CASAForm/CheckboxOption";
import DependentCheckboxQuestions from "../../../../components/CASAForm/QuestionGroups/DependentCheckboxQuestions";
import {AUcallout} from "@gov.au/callout";

/* DEFAULT
 * The stepNavigation component
 *
 * @param  {string}    ratingEntry                  - this defines the type of form
 * @param  {function}  returnAdditionalDetails      - returns the question data to the step
 * @param  {object}    existingAnswers              - list of exisiting answers
 */
export const MPLSummaryHeaderVariations = (courseOutcome) => {
    switch (courseOutcome) {
        case 'A1' :
        case 'A2' :
        case 'A3' :
        case 'A4' :
        case 'B2' :
        case 'B1.3':
        case 'B1.4': {
            return (
                [
                    {title: "Student name", key: "name", width: "20"},
                    {title: "Student ARN", key: "id", width: "20"},
                    {title: "Date requirements were met", key: "dateRequirementsMet", width: "20"},
                    {title: "Date practical requirements were met", key: "datePracticalRequirementsMet", width: "20"},
                    {title: "Recognition of prior learning", key: "recognitionOfPriorLearning", width: "20"}
                ]
            )
        }
        case 'B1.1' :
        case 'B1.2' : {
            return (
                [
                    {title: "Student name", key: "name", width: "20"},
                    {title: "Student ARN", key: "id", width: "20"},
                    {title: "Optional units of competency", key: "optionalUnitsOfCompetency", width: "20"},
                    {title: "Date requirements were met", key: "dateRequirementsMet", width: "20"},
                    {
                        title: "Date practical requirements were met",
                        key: "datePracticalRequirementsMet",
                        width: "20"
                    },
                    {title: "Recognition of prior learning", key: "recognitionOfPriorLearning", width: "20"}
                ]
            )
        }
        case 'Theory': {
            return (
                [
                    {title: "Student name", key: "name", width: "20"},
                    {title: "Student ARN", key: "id", width: "20"},
                    {title: "Date theory requirements were met", key: "dateRequirementsMet", width: "20"},
                    {title: "Recognition of prior learning", key: "recognitionOfPriorLearning", width: "20"}
                ]
            )
        }
        case 'Practical': {
            return (
                [
                    {title: "Student name", key: "name", width: "20"},
                    {title: "Student ARN", key: "id", width: "20"},
                    {title: "Date practical requirements were met", key: "datePracticalRequirementsMet", width: "20"},
                    {title: "Recognition of prior learning", key: "recognitionOfPriorLearning", width: "20"}
                ]
            )
        }
        case 'Theory/Practical': {
            return (
                [
                    {title: "Student name", key: "name", width: "20"},
                    {title: "Student ARN", key: "id", width: "20"},
                    {title: "Date theory requirements were met", key: "dateRequirementsMet", width: "20"},
                    {title: "Date practical requirements were met", key: "datePracticalRequirementsMet", width: "20"},
                    {title: "Recognition of prior learning", key: "recognitionOfPriorLearning", width: "20"}
                ]
            )
        }
        default: {
            return (
                [
                    {title: "Student name", key: "name", width: "20", extendRow: true},
                    {title: "Student ARN", key: "id", width: "20", extendRow: true},
                    {title: "Exclusion categories", key: "exclusionCategoriesToBeRemoved", width: "20"},
                    {title: "Exclusion/limitation codes", key: "mappedElement", width: "20"},
                    {title: "Date requirements were met", key: "dateRequirementsMet", width: "10", extendRow: true},
                    {title: "Recognition of prior learning", key: "recognitionOfPriorLearning", width: "10", extendRow: true}
                ]
            )
        }
    }
};

/* DEFAULT
 * The MPLStudentDetailsVariations component
 *
 * @param  {string}    courseOutcome                  - this defines the type of form
 * @param  {function}  returnAdditionalDetails      - returns the question data to the step
 * @param  {object}    existingAnswers              - list of exisiting answers
 */
export const MPLStudentDetailsVariations = ({courseOutcome, returnAdditionalDetails, existingAnswers, removeQuestionData, showFileUpload, showHideFileUpload, showError, props}) => {

    const getExistingAnswer = (questionId) => {
        if (Object.keys(existingAnswers).length !== 0) {
            return (existingAnswers[questionId]?.answer)
        }
    }

    const updateRequirementsLabel = (type) => {
        let allowedTypes = "Theory";
        return allowedTypes.filter(item => item === type).length > 0;
    }

    const enablePracticalDateField = (type) => {
        let allowedTypes = ["A1", "A2", "A3", "A4", "B1.1", "B1.2", "B1.3", "B1.4", "B2", "Practical", "Theory/Practical"];
        return allowedTypes.filter(item => item === type).length > 0;
    }

    // For dependent checkbox child groups
    const getExistingChildAnswers = (parentId) => {
        const parentAnswers = getExistingAnswer(parentId)
        let aggregateChildAnswerObject = []

        if (parentAnswers?.checkboxes) {
            Object.values(parentAnswers.checkboxes).map(checkbox => {
                if (checkbox.check) {
                    let childQuestion;
                    if (Object.keys(existingAnswers).length !== 0) {
                        childQuestion = (existingAnswers['child-' + checkbox.value])
                    }
                    if (childQuestion) {
                        aggregateChildAnswerObject.push(childQuestion)
                    }
                }
            })

            return aggregateChildAnswerObject;
        }
        return null
    }

    const generateAddtionalDetailsQuestions = (type) => {
        return (
            <Fragment>
                <Question
                    id="optionalUnitsOfCompetency"
                    type="checkbox"
                    label="Optional units of competency"
                    stepid="addStudent"
                    allowFutureDates={false}
                    returnQuestionData={returnAdditionalDetails}
                    showError={showError}
                    show={type === "B1.1"}
                    mandatory={false}
                    existingAnswer={getExistingAnswer('optionalUnitsOfCompetency')}>
                    <CheckboxOption
                        type="checkbox"
                        questionid="optionalUnitsOfCompetency"
                        name="fabricSurfaces"
                        id="fabricSurfaces"
                        value="fabricSurfaces"
                        optionLabel="Fabric Surfaces"
                        key="fabricSurfaces">
                    </CheckboxOption>
                    <CheckboxOption
                        type="checkbox"
                        questionid="optionalUnitsOfCompetency"
                        name="woodenStructures"
                        id="woodenStructures"
                        value="woodenStructures"
                        optionLabel="Wooden Structures"
                        key="woodenStructures">
                    </CheckboxOption>
                    <CheckboxOption
                        type="checkbox"
                        questionid="optionalUnitsOfCompetency"
                        name="propellers"
                        id="propellers"
                        value="propellers"
                        optionLabel="Propellers"
                        key="propellers">
                    </CheckboxOption>
                </Question>

                <Question
                    id="optionalUnitsOfCompetency"
                    type="checkbox"
                    label="Optional units of competency"
                    stepid="addStudent"
                    allowFutureDates={false}
                    returnQuestionData={returnAdditionalDetails}
                    showError={showError}
                    show={type === "B1.2"}
                    mandatory={false}
                    existingAnswer={getExistingAnswer('optionalUnitsOfCompetency')}>
                    <CheckboxOption
                        type="checkbox"
                        questionid="optionalUnitsOfCompetency"
                        name="fabricSurfaces"
                        id="fabricSurfaces"
                        value="fabricSurfaces"
                        optionLabel="Fabric Surfaces"
                        key="fabricSurfaces">
                    </CheckboxOption>
                    <CheckboxOption
                        type="checkbox"
                        questionid="optionalUnitsOfCompetency"
                        name="woodenStructures"
                        id="woodenStructures"
                        value="woodenStructures"
                        optionLabel="Wooden Structures"
                        key="woodenStructures">
                    </CheckboxOption>
                </Question>

                <DependentCheckboxQuestions
                    id="exclusionCategoriesToBeRemoved"
                    key="exclusionCategoriesToBeRemoved"
                    stepid="addStudent"
                    label="Please select the categories that the exclusions are to be removed from"
                    hintText="You may select more than one"
                    options={props.esbReferenceData.referenceData}
                    showError={showError}
                    show={type === "categoryRemoval"} //Change the component to include show/hide functionality
                    returnQuestionData={returnAdditionalDetails}
                    removeQuestionData={removeQuestionData}
                    existingAnswer={getExistingAnswer('exclusionCategoriesToBeRemoved')}
                    childExistingAnswers={getExistingChildAnswers('exclusionCategoriesToBeRemoved')}/>

                <Question
                    id="dateRequirementsMet"
                    type="date"
                    label={type === "Theory" || type === "Theory/Practical" ? "Date theory requirements were met" : "Date requirements were met"}
                    stepid="addStudent"
                    allowFutureDates={false}
                    showError={showError}
                    show={type !== "Practical"}
                    returnQuestionData={returnAdditionalDetails}
                    existingAnswer={getExistingAnswer('dateRequirementsMet')}/>

                <Question
                    id="datePracticalRequirementsMet"
                    type="date"
                    label="Date practical requirements were met"
                    stepid="addStudent"
                    allowFutureDates={false}
                    show={enablePracticalDateField(type)}
                    showError={showError}
                    returnQuestionData={returnAdditionalDetails}
                    existingAnswer={getExistingAnswer('datePracticalRequirementsMet')}/>

                {/* <Question
                    id="dateRequirementsMet"
                    type="date"
                    label="Date requirements were met"
                    stepid="addStudent"
                    allowFutureDates={false}
                    showError={showError}
                    show={type === "categoryRemoval"}
                    returnQuestionData={returnAdditionalDetails}
                    existingAnswer={getExistingAnswer('dateRequirementsMet')}/> */}

                <Question
                    id="recognitionOfPriorLearning"
                    type="radio"
                    label="Is this report based on an Recognition of Prior Learning assessment of the students qualifications?"
                    stepid="addStudent"
                    returnQuestionData={returnAdditionalDetails}
                    showError={showError}
                    existingAnswer={getExistingAnswer('recognitionOfPriorLearning')}
                    onChangeFunction={showHideFileUpload}
                    radioSet={[
                        {
                            name: "yes",
                            id: "yes",
                            value: "yes",
                            optionLabel: "Yes"
                        },
                        {
                            name: "no",
                            id: "no",
                            value: "no",
                            optionLabel: "No"
                        }
                    ]}/>

                <AUcallout
                    title="Please note" srOnlyTitle className={`${!showFileUpload && "hide"}`}>
                    <h3>Please note</h3>
                    <p>Notifications with attachments cannot be saved and completed later.</p>
                </AUcallout>

                <Question
                    id="studentQualificationEvidence"
                    type="filePicker"
                    label="Upload evidence of student's qualifications"
                    toolTip="2 files per student can be uploaded. Accepted file types are PDF documents (.pdf), DOC documents (.doc, .docx) and EXCEL documents (.xls, .xlsx, .csv). Maximum file size is 2mb per file."
                    stepid="addStudent"
                    show={showFileUpload}
                    allowFutureDates={false}
                    returnQuestionData={returnAdditionalDetails}
                    showError={showError}
                    existingAnswer={getExistingAnswer('studentQualificationEvidence')}
                    removeQuestionData={removeQuestionData}
                    maxFileSize={2048}
                    maxNoOfFilesAllowed={2}
                    errorMessages={{
                        maxSizeReached: "The document exceeds the maximum file size of 2MB",
                        invalidFileName: "Uploaded file name contains unsupported characters. Supported characters are alphabets, numbers and special characters , _ -. Please check the file you are trying to upload.",
                        maxNoOfFilesReached: "The maximum of 2 documents have been reached",
                        invalidFileType: "The file type is not supported",
                    }}/>
            </Fragment>
        )
    }
    return generateAddtionalDetailsQuestions(courseOutcome)
};

MPLStudentDetailsVariations.propTypes = {
    returnAdditionalDetails: PropTypes.func.isRequired,
    esbReferenceData: PropTypes.object
};